import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useOrganizationInvitations } from '@/hooks/useOrganizationInvitations';
import { useToast } from '@/hooks/use-toast';
import { UserPlus } from 'lucide-react';

interface OrganizationInviteDialogProps {
  organizationId: string;
}

export function OrganizationInviteDialog({ organizationId }: OrganizationInviteDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [orgRole, setOrgRole] = useState<'member' | 'super_user' | 'owner'>('member');
  const [submitting, setSubmitting] = useState(false);
  const { sendInvitation } = useOrganizationInvitations(organizationId);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !orgRole) return;

    try {
      setSubmitting(true);
      await sendInvitation(email, orgRole);
      setEmail('');
      setOrgRole('member');
      setOpen(false);
    } catch (error: any) {
      // Error already handled by hook via toast
      console.error('Failed to send invitation:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to add a new member to your organization
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="orgRole">Organization Role</Label>
            <Select value={orgRole} onValueChange={(value) => setOrgRole(value as 'member' | 'super_user' | 'owner')} required>
              <SelectTrigger id="orgRole">
                <SelectValue placeholder="Select organization role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="super_user">Super User</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              <strong>Member:</strong> Can create and manage their own jobs.<br/>
              <strong>Super User:</strong> Can manage all organization jobs and payment methods.
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
